import React from 'react';
import { Inspector, Tabs } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { WIDGET_TYPE } from '../../../../types';
import { CollectionWidgetSettings } from './CollectionWidgetSettings';
import { SpacerWidgetSettings } from './SpacerWidgetSettings';
import { ColorPaletteWidgetSettings } from './ColorPaletteWidgetSettings';
import { ImageWidgetSettings } from './ImageWidgetSettings';
import { useActiveWidget } from '../../../../queries/guides';
import { InspectorSetting } from '../../../../constants/guides';
import { SectionSettings } from '../SectionSettings/SectionSettings';
import SectionSettingsFooter from '../SectionSettings/SectionSettingsFooter';
import WidgetSettingsFooter from './WidgetSettingsFooter';
import { VideoWidgetSettings } from './VideoWidgetSettings';
import { AudioWidgetSettings } from './AudioWidgetSettings';
import { EmptyWidgetSelection } from './EmptyWidgetSelection';
import { ComingSoonWidget } from './ComingSoonWidget';
import { StyledTabsShadow } from '../../Styles';
import { useGuideStore } from '../../../../stores/guideStore';
import { useInspectorStore } from '../../../../stores/inspectorStore';
import { ButtonWidgetSettings } from './ButtonWidgetSettings';
import CardWidgetSettings from './CardWidgetSettings';
import ButtonSettingsFooter from './ButtonWidgetSettings/Footer';
import CardSettingsFooter from './CardWidgetSettings/Footer';
import { YoutubeWidgetSettings } from './YoutubeWidgetSettings';
import { VimeoWidgetSettings } from './VimeoWidgetSettings';
import { FigmaWidgetSettings } from './FigmaWidgetSettings';
import { TeaWidgetSettings } from './TeaWidgetSettings';

export const WidgetSettings: React.FC = () => {
  const widget = useActiveWidget();
  const { translate } = useTranslate();
  const activeSectionId = useGuideStore(s => s.activeSectionId);
  const setActiveSectionId = useGuideStore(s => s.setActiveSectionId);
  const activeInspectorSetting = useInspectorStore(
    s => s.activeInspectorSetting,
  );
  const setActiveInspectorSetting = useInspectorStore(
    s => s.setActiveInspectorSetting,
  );
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const setWidgetSettings = useInspectorStore(s => s.setWidgetSettings);

  React.useEffect(() => {
    if (widgetSettings?.sectionId === activeSectionId) {
      setActiveSectionId(widgetSettings.sectionId);
    } else {
      setWidgetSettings(null);
      setActiveInspectorSetting(InspectorSetting.SECTION_VIEW);
    }
  }, [widgetSettings?.sectionId, activeSectionId]);

  const renderWidgetSettings = (widgetType: WIDGET_TYPE) => {
    switch (widgetType) {
      case WIDGET_TYPE.TEA:
        return <TeaWidgetSettings />;
      case WIDGET_TYPE.COLLECTION:
        return <CollectionWidgetSettings />;
      case WIDGET_TYPE.SPACER:
        return <SpacerWidgetSettings />;
      case WIDGET_TYPE.COLOR_PALETTE:
        return <ColorPaletteWidgetSettings />;
      case WIDGET_TYPE.IMAGE:
        return <ImageWidgetSettings />;
      case WIDGET_TYPE.VIDEO:
        return <VideoWidgetSettings />;
      case WIDGET_TYPE.AUDIO:
        return <AudioWidgetSettings />;
      case WIDGET_TYPE.TABLE:
      case WIDGET_TYPE.TEXT:
      case WIDGET_TYPE.TITLE:
      case WIDGET_TYPE.PERSONAL_FILE:
        return <ComingSoonWidget />;
      case WIDGET_TYPE.BUTTON:
        return <ButtonWidgetSettings />;
      case WIDGET_TYPE.CARD:
        return <CardWidgetSettings />;
      case WIDGET_TYPE.VIMEO:
        return <VimeoWidgetSettings />;
      case WIDGET_TYPE.YOUTUBE:
        return <YoutubeWidgetSettings />;
      case WIDGET_TYPE.FIGMA:
        return <FigmaWidgetSettings />;
      default:
        return <EmptyWidgetSelection />;
    }
  };

  const renderWidgetSettingsFooter = (widgetType: WIDGET_TYPE) => {
    switch (widgetType) {
      case WIDGET_TYPE.CARD:
        return <CardSettingsFooter />;
      case WIDGET_TYPE.BUTTON:
        return <ButtonSettingsFooter />;
      default:
        return <WidgetSettingsFooter />;
    }
  };

  return (
    <>
      <StyledTabsShadow>
        <Inspector.Tabs>
          <Tabs>
            <Tabs.Tab
              label={translate('SECTION_INSPECTOR.SECTION_TITLE')}
              isActive={
                activeInspectorSetting === InspectorSetting.SECTION_VIEW
              }
              onClick={() => {
                setActiveInspectorSetting(InspectorSetting.SECTION_VIEW);
              }}
            />
            <Tabs.Tab
              label={translate('SECTION_INSPECTOR.WIDGET_TITLE')}
              isActive={activeInspectorSetting === InspectorSetting.WIDGET_VIEW}
              onClick={() => {
                setActiveInspectorSetting(InspectorSetting.WIDGET_VIEW);
              }}
            />
          </Tabs>
        </Inspector.Tabs>
      </StyledTabsShadow>
      <Inspector.Content>
        {activeInspectorSetting === InspectorSetting.SECTION_VIEW ? (
          <SectionSettings />
        ) : (
          renderWidgetSettings(widget?.type)
        )}
      </Inspector.Content>
      <Inspector.Footer>
        {activeInspectorSetting === InspectorSetting.SECTION_VIEW ? (
          <SectionSettingsFooter />
        ) : (
          !!widgetSettings && renderWidgetSettingsFooter(widget?.type)
        )}
      </Inspector.Footer>
    </>
  );
};
